import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authConfig, { type AuthMode } from '@/config/auth.config'
import { login as loginApi, refreshToken as refreshApi, logout as logoutApi, type LoginResponse } from '@/api/auth'
import {
  loadTokens,
  saveTokens,
  clearTokens,
  getStorageScope,
  setStorageScope,
  getAuthMode,
  setAuthMode,
  isAccessTokenExpired,
  isRefreshTokenExpired,
  shouldRefreshSoon,
  type StorageScope,
  type AuthUserInfo,
  type TenantInfo
} from '@/utils/authStorage'

interface LoginOptions {
  rememberMe?: boolean
  deviceId?: string
  deviceName?: string
  mode?: AuthMode
}

export const useAuthStore = defineStore('auth', () => {
  let refreshPromise: Promise<LoginResponse> | null = null
  const accessToken = ref('')
  const refreshToken = ref('')
  const expiresAt = ref(0)
  const refreshExpiresAt = ref(0)
  const user = ref<AuthUserInfo | undefined>(undefined)
  const currentTenantId = ref<string | null>(null)
  const mode = ref<AuthMode>(getAuthMode())
  const storageScope = ref<StorageScope>(getStorageScope())
  const initialized = ref(false)

  const isAccessTokenValid = computed(() => !!accessToken.value && !isAccessTokenExpired(expiresAt.value))
  const isRefreshTokenValid = computed(() => !!refreshToken.value && !isRefreshTokenExpired(refreshExpiresAt.value))
  const isAuthenticated = computed(() => isAccessTokenValid.value || isRefreshTokenValid.value)

  /** 可用租户列表（登录身份路由数据源；无 available_tenants 时为空数组）。 */
  const availableTenants = computed<TenantInfo[]>(() => user.value?.available_tenants ?? [])

  /** 当前租户上下文：优先 currentTenantId，否则自动取唯一非全局租户（单租户自动进入）。 */
  const currentTenant = computed<TenantInfo | null>(() => {
    const tenants = availableTenants.value.filter((t) => !t.is_global)
    if (currentTenantId.value) {
      return tenants.find((t) => t.tenant_id === currentTenantId.value) ?? null
    }
    return tenants.length === 1 ? (tenants[0] ?? null) : null
  })

  function applyLoginResponse(res: LoginResponse) {
    accessToken.value = res.accessToken
    refreshToken.value = res.refreshToken
    expiresAt.value = Date.now() + res.expiresIn * 1000
    // refresh 响应 refreshExpiresIn=0（不续期）→ 保留现有 refreshExpiresAt
    if (res.refreshExpiresIn > 0) {
      refreshExpiresAt.value = Date.now() + res.refreshExpiresIn * 1000
    }
    // refresh 响应不含完整 user（见 api/auth.ts refreshToken）→ 保留旧 user，避免租户上下文 UI 退化
    if (res.user && (res.user.id || res.user.username)) {
      user.value = res.user
    }
    // 单租户自动恢复上下文；多租户待选择页确认后设置
    if (user.value?.available_tenants) {
      const nonGlobal = user.value.available_tenants.filter((t) => !t.is_global)
      currentTenantId.value = nonGlobal.length === 1 ? (nonGlobal[0]?.tenant_id ?? null) : currentTenantId.value
    }

    saveTokens({
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
      expiresAt: expiresAt.value,
      refreshExpiresAt: refreshExpiresAt.value,
      user: user.value,
      scope: storageScope.value,
      mode: mode.value,
      currentTenantId: currentTenantId.value
    })
  }

  function initAuth() {
    const stored = loadTokens()
    if (stored) {
      accessToken.value = stored.accessToken
      refreshToken.value = stored.refreshToken
      expiresAt.value = stored.expiresAt
      refreshExpiresAt.value = stored.refreshExpiresAt
      user.value = stored.user
      mode.value = stored.mode
      storageScope.value = stored.scope
      currentTenantId.value = stored.currentTenantId ?? null
    }
    initialized.value = true
  }

  function setMode(newMode: AuthMode) {
    mode.value = newMode
    setAuthMode(newMode)
  }

  function setRememberMe(rememberMe: boolean) {
    if (mode.value !== 'web') return
    // 统一 localStorage 共享登录态；"记住我"仅影响 refresh_token 时长（后端按 remember_me 签发）
    const scope: StorageScope = rememberMe ? 'persistent' : 'session'
    storageScope.value = scope
    setStorageScope(scope)
  }

  async function login(username: string, password: string, options: LoginOptions = {}) {
    const targetMode = options.mode || mode.value || authConfig.mode
    setMode(targetMode)

    if (targetMode === 'web') {
      setRememberMe(options.rememberMe ?? authConfig.webDefaultRememberMe)
    } else {
      storageScope.value = 'persistent'
      setStorageScope('persistent')
    }

    const response = await loginApi({
      username,
      password,
      rememberMe: options.rememberMe,
      deviceId: options.deviceId,
      deviceName: options.deviceName,
      clientType: targetMode
    })

    applyLoginResponse(response)
    return response
  }

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      throw new Error('No refresh token')
    }
    if (!refreshPromise) {
      refreshPromise = refreshApi({
        refreshToken: refreshToken.value,
        clientType: mode.value
      })
        .then((response) => {
          applyLoginResponse(response)
          return response
        })
        .finally(() => {
          refreshPromise = null
        })
    }

    return refreshPromise
  }

  async function ensureValidAccessToken() {
    if (isAccessTokenValid.value && !shouldRefreshSoon(expiresAt.value, authConfig.refreshThresholdSeconds)) {
      return
    }
    if (!isRefreshTokenValid.value) {
      throw new Error('Refresh token expired')
    }
    await refreshAccessToken()
  }

  async function logout() {
    try {
      await logoutApi()
    } catch {
      // ignore
    }
    clearTokens()
    accessToken.value = ''
    refreshToken.value = ''
    expiresAt.value = 0
    refreshExpiresAt.value = 0
    user.value = undefined
    currentTenantId.value = null
  }

  /** 切换当前操作租户身份：调用后端校验岗位 → 更新全局上下文。 */
  async function switchCurrentTenant(tenantId: string) {
    const { switchTenant: switchTenantApi } = await import('@/api/auth')
    const res = await switchTenantApi(tenantId)
    currentTenantId.value = res.tenant_id
    saveTokens({
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
      expiresAt: expiresAt.value,
      refreshExpiresAt: refreshExpiresAt.value,
      user: user.value,
      scope: storageScope.value,
      mode: mode.value,
      currentTenantId: currentTenantId.value
    })
    return res
  }

  /**
   * 局部更新当前用户信息并持久化（T-077 / A6）。
   *
   * 用途：个人中心 `PUT /auth/me` 成功后，让头部/侧栏等消费 `authStore.user` 的位置立即同步，
   * 并把新值写回 storage（否则刷新后仍显示旧 display_name —— `initAuth()` 从 storage 恢复）。
   * 保持"storage 只由本 store 写入"的既有约定，避免页面直接碰 storage。
   */
  function patchUser(patch: Partial<AuthUserInfo>) {
    if (!user.value) return
    user.value = { ...user.value, ...patch }
    saveTokens({
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
      expiresAt: expiresAt.value,
      refreshExpiresAt: refreshExpiresAt.value,
      user: user.value,
      scope: storageScope.value,
      mode: mode.value,
      currentTenantId: currentTenantId.value
    })
  }

  return {
    accessToken,
    refreshToken,
    expiresAt,
    refreshExpiresAt,
    user,
    mode,
    storageScope,
    initialized,
    currentTenantId,
    availableTenants,
    currentTenant,
    isAuthenticated,
    isAccessTokenValid,
    isRefreshTokenValid,
    initAuth,
    setMode,
    setRememberMe,
    login,
    refreshAccessToken,
    ensureValidAccessToken,
    switchCurrentTenant,
    patchUser,
    logout
  }
})
